import React, { useEffect } from 'react';
import { Box, Button, HStack, Input, Text, Stack } from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Field } from '@chakra-ui/react';
import { Switch } from '@/components/ui/switch';

import { Divider } from '@/components/divider';
import { toaster } from '@/components/ui/toaster';
import { CreateLocationBody } from '../locations.types';
import { AuthType } from '../locations.types';
import { useCreateLocation, useFetchLocationInfo } from '../locations.hook';
import { Badge, VStack } from '@chakra-ui/react';
import { FaDownload, FaEye, FaEyeSlash } from 'react-icons/fa';

// Enhanced schema for the form including all fields from screenshot
import { z } from 'zod';
import { useOrgLocation } from '@/context/organization-location-context';

const locationFormSchema = z.object({
  organisation_id: z.string(),
  name: z
    .string()
    .min(1, 'Location name is required')
    .max(100, 'Location name must be less than 100 characters'),
  email: z.string().email('Invalid email format').optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  address: z
    .string()
    .max(255, 'Address must be less than 255 characters')
    .optional()
    .or(z.literal('')),
  postal_code: z
    .string()
    .max(20, 'Postal code must be less than 20 characters')
    .optional()
    .or(z.literal('')),
  country_code: z
    .string()
    .length(2, 'Country code must be exactly 2 characters')
    .optional()
    .or(z.literal('')),
  language_code: z
    .string()
    .length(2, 'Language code must be exactly 2 characters')
    .optional()
    .or(z.literal('')),
  url: z.string().url('Invalid URL format').optional().or(z.literal('')),
  facebook_url: z
    .string()
    .url('Invalid Facebook URL')
    .optional()
    .or(z.literal('')),
  instagram_url: z
    .string()
    .url('Invalid Instagram URL')
    .optional()
    .or(z.literal('')),
  twitter_url: z
    .string()
    .url('Invalid Twitter URL')
    .optional()
    .or(z.literal('')),
  linkedin_url: z
    .string()
    .url('Invalid LinkedIn URL')
    .optional()
    .or(z.literal('')),
  youtube_url: z
    .string()
    .url('Invalid YouTube URL')
    .optional()
    .or(z.literal('')),
  tiktok_url: z.string().url('Invalid TikTok URL').optional().or(z.literal('')),
  snapchat_url: z
    .string()
    .url('Invalid Snapchat URL')
    .optional()
    .or(z.literal('')),
  spotify_url: z
    .string()
    .url('Invalid Spotify URL')
    .optional()
    .or(z.literal('')),
  auth_id: z.string().optional(),
  agent_delay_time: z.number().default(0).optional(),
  agent_delay_after_employee: z.boolean().default(false).optional(),
  agent_delay_after_employee_time: z.number().default(0).optional(),
  agent_delay: z.boolean().default(false).optional(),
  max_interaction_per_session_sales_widget: z.number().default(0).optional(),
  max_interaction_per_session_sales_whatsapp: z.number().default(0).optional(),
  max_interaction_per_session_retention_whatsapp: z
    .number()
    .default(0)
    .optional(),
  agent_default_active: z.boolean().default(false).optional(),
  max_interaction_per_month: z.number().default(0).optional(),
  max_interaction_per_session: z.number().default(0).optional(),
  pay_per_use: z.boolean().default(false).optional(),
});

type LocationFormData = z.infer<typeof locationFormSchema>;

interface LocationCreateFormProps {
  onSuccess?: (location: CreateLocationBody) => void;
  onCancel?: () => void;
  onAddAuthType?: () => void;
  onResetAuth?: () => void;
  authTypes?: AuthType[];
}

export const LocationCreateForm: React.FC<LocationCreateFormProps> = ({
  onSuccess,
  onCancel,
  onAddAuthType,
  onResetAuth,
  authTypes = [],
}) => {
  const { selectedOrganisation } = useOrgLocation();
  const [showAuthDetails, setShowAuthDetails] = React.useState(false);
  const fetchLocationInfoMutation = useFetchLocationInfo();
  const form = useForm<LocationFormData>({
    resolver: zodResolver(locationFormSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      postal_code: '',
      country_code: '',
      language_code: '',
      url: '',
      facebook_url: 'https://facebook.com/your-',
      instagram_url: 'https://instagram.com/your',
      twitter_url: 'https://twitter.com/your-han',
      linkedin_url: 'https://linkedin.com/compa',
      youtube_url: 'https://youtube.com/your-c',
      tiktok_url: 'https://tiktok.com/@your-ac',
      snapchat_url: 'https://snapchat.com/add/y',
      spotify_url: 'https://open.spotify.com/yo',
      auth_id: '',
      organisation_id: selectedOrganisation?.id,
      agent_delay_after_employee: false,
      agent_delay_after_employee_time: 0,
      agent_delay: false,
      agent_delay_time: 0,
      agent_default_active: false,
      max_interaction_per_month: 0,
      max_interaction_per_session: 0,
      pay_per_use: false,
      max_interaction_per_session_sales_widget: undefined,
      max_interaction_per_session_sales_whatsapp: undefined,
      max_interaction_per_session_retention_whatsapp: undefined,
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  const { mutateAsync: createLocation } = useCreateLocation({
    onSuccess: (location) => {
      toaster.create({
        title: 'Location Created',
        description: `Location has been created successfully.`,
        type: 'success',
      });

      onSuccess?.(location);
    },
    onError: () => {
      toaster.create({
        title: 'Creation Failed',
        description: 'Failed to create location. Please try again.',
        type: 'error',
      });
    },
  });

  // Get the latest created auth type (most recent one)
  const latestAuthType =
    authTypes.length > 0 ? authTypes[authTypes.length - 1] : null;

  const onSubmit = async (data: LocationFormData) => {
    await createLocation(data);
  };

  // Update form auth_id when authTypes changes (new auth type added)
  React.useEffect(() => {
    if (latestAuthType) {
      console.log('latestAuthType useEffect', latestAuthType);
      form.setValue('auth_id', latestAuthType.id);
    }
  }, [authTypes.length, latestAuthType, form]);

  const handleFetchLocationInfo = async () => {
    console.log('latestAuthType handleFetchLocationInfo', latestAuthType);
    if (!latestAuthType) return;

    try {
      const locationInfo = await fetchLocationInfoMutation.mutateAsync({
        authType: latestAuthType,
      });

      // Update form fields with fetched data
      form.setValue('name', locationInfo.name || '');
      form.setValue('address', locationInfo.address || '');
      form.setValue('postal_code', locationInfo.postal_code || '');
      form.setValue('country_code', locationInfo.country_iso2 || '');
      form.setValue('language_code', locationInfo.language_iso2 || '');
      form.setValue('phone', locationInfo.phone || '');
      form.setValue('email', locationInfo.email || '');
      form.setValue('url', locationInfo.url || '');
      form.setValue('facebook_url', locationInfo.facebook_url || '');
      form.setValue('instagram_url', locationInfo.instagram_url || '');
      form.setValue('twitter_url', locationInfo.twitter_url || '');
      form.setValue('linkedin_url', locationInfo.linkedin_url || '');
      form.setValue('youtube_url', locationInfo.youtube_url || '');
      form.setValue('tiktok_url', locationInfo.tiktok_url || '');
      form.setValue('snapchat_url', locationInfo.snapchat_url || '');
      form.setValue('spotify_url', locationInfo.spotify_url || '');
      form.setValue(
        'agent_default_active',
        !!locationInfo.agent_default_active,
      );
      form.setValue('agent_delay', !!locationInfo.agent_delay);
      form.setValue('agent_delay_time', locationInfo.agent_delay);
      form.setValue(
        'agent_delay_after_employee',
        !!locationInfo.agent_delay_after_employee,
      );
      form.setValue(
        'agent_delay_after_employee_time',
        locationInfo.agent_delay_after_employee || 0,
      );
      form.setValue(
        'max_interaction_per_session_sales_widget',
        locationInfo.max_interaction_per_session_sales_widget ?? 0,
      );
      form.setValue(
        'max_interaction_per_session_sales_whatsapp',
        locationInfo.max_interaction_per_session_sales_whatsapp ?? 0,
      );
      form.setValue(
        'max_interaction_per_session_retention_whatsapp',
        locationInfo.max_interaction_per_session_retention_whatsapp ?? 0,
      );
      form.setValue(
        'max_interaction_per_month',
        locationInfo.max_interaction_per_month ?? 0,
      );
      form.setValue(
        'max_interaction_per_session',
        locationInfo.max_interaction_per_session ?? 0,
      );
      form.setValue('pay_per_use', !!locationInfo.pay_per_use);

      toaster.create({
        title: 'Location Info Fetched Successfully',
        description:
          'Location information has been retrieved and auto-filled in the form.',
        type: 'success',
      });
    } catch (error) {
      console.error('Fetch location info error:', error);

      let errorMessage =
        'An unexpected error occurred while fetching location information.';

      if (error instanceof Error) {
        // Handle specific error types
        if (error.message.includes('CORS')) {
          errorMessage =
            'CORS error: The API server is not allowing requests from this domain. Please check the server configuration.';
        } else if (error.message.includes('Authentication failed')) {
          errorMessage =
            'Authentication failed. Please check your username and password credentials.';
        } else if (error.message.includes('Access denied')) {
          errorMessage =
            'Access denied. Your credentials do not have permission to access this resource.';
        } else if (error.message.includes('Network error')) {
          errorMessage =
            'Network error: Unable to connect to the API server. Please check your internet connection.';
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage =
            'Connection failed: Unable to reach the API server. This could be due to CORS policy or network issues.';
        } else {
          errorMessage = error.message;
        }
      }

      toaster.create({
        title: 'Failed to Fetch Location Info',
        description: errorMessage,
        type: 'error',
        duration: 8000, // Show error longer so user can read it
      });
    }
  };

  useEffect(() => {
    const agentDelayTime = form.watch('agent_delay_time');
    const agentDelayAfterEmployeeTime = form.watch(
      'agent_delay_after_employee_time',
    );

    form.setValue('agent_delay', !!agentDelayTime);
    form.setValue('agent_delay_after_employee', !!agentDelayAfterEmployeeTime);
  }, [
    form.watch('agent_delay_time'),
    form.watch('agent_delay_after_employee_time'),
  ]);

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack align="stretch">
        {/* Header */}
        <Box>
          <HStack justify="space-between" align="center" mb={4}>
            {!latestAuthType && (
              <Button
                variant="outline"
                size="sm"
                borderRadius="xl"
                bg="blue.50"
                borderColor="blue.300"
                color="blue.600"
                _hover={{
                  bg: 'blue.100',
                  borderColor: 'blue.400',
                  color: 'blue.700',
                }}
                onClick={onAddAuthType}
              >
                Add Basic Auth (Optional)
              </Button>
            )}
          </HStack>
        </Box>
        {/* Authentication Section */}
        {latestAuthType && (
          <Box>
            <Text fontSize="md" fontWeight="medium" mb={4}>
              Authentication Configuration to fetch location info
            </Text>
            <HStack justify="space-between" align="center" mb={4}>
              <HStack gap={3}>
                <Button
                  variant="outline"
                  size="sm"
                  borderRadius="xl"
                  bg="red.50"
                  borderColor="red.300"
                  color="red.600"
                  _hover={{
                    bg: 'red.100',
                    borderColor: 'red.400',
                    color: 'red.700',
                  }}
                  onClick={() => {
                    // Reset the form auth_id field
                    form.setValue('auth_id', '');
                    // Call parent reset handler
                    onResetAuth?.();
                  }}
                >
                  Reset Auth
                </Button>
                <Box>
                  <HStack gap={3}>
                    <Text fontSize="sm" color="gray.600">
                      Selected:
                    </Text>
                    <Badge colorScheme="blue" variant="outline">
                      {latestAuthType.name}
                    </Badge>
                  </HStack>
                </Box>
              </HStack>
              <Button
                variant="outline"
                size="sm"
                borderRadius="xl"
                bg="blue.50"
                borderColor="blue.300"
                color="blue.600"
                _hover={{
                  bg: 'blue.100',
                  borderColor: 'blue.400',
                  color: 'blue.700',
                }}
                onClick={onAddAuthType}
              >
                + Add Basic Auth
              </Button>
            </HStack>

            {/* Auth Type Details */}
            <Box p={4} bg="gray.50" borderRadius="md" mb={4}>
              <VStack align="stretch" gap={3}>
                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="medium">
                    Template:
                  </Text>
                  <Badge colorScheme="green">Basic Auth</Badge>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="medium">
                    Base URL:
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {latestAuthType.base_url}
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="medium">
                    Username:
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {latestAuthType?.username}
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="medium">
                    Password:
                  </Text>
                  <HStack>
                    <Text fontSize="sm" color="gray.600">
                      {showAuthDetails ? latestAuthType?.password : '••••••••'}
                    </Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setShowAuthDetails(!showAuthDetails)}
                    >
                      {showAuthDetails ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                  </HStack>
                </HStack>
              </VStack>
            </Box>

            {/* Fetch Location Info Button */}
            <Button
              variant="solid"
              size="md"
              borderRadius="xl"
              bg="green.500"
              color="white"
              _hover={{
                bg: 'green.600',
                transform: 'translateY(-1px)',
                boxShadow: 'lg',
              }}
              _active={{
                bg: 'green.700',
                transform: 'translateY(0)',
              }}
              transition="all 0.2s"
              fontWeight="semibold"
              loading={fetchLocationInfoMutation.isPending}
              loadingText="Fetching..."
              onClick={handleFetchLocationInfo}
              width="full"
            >
              <FaDownload />
              Fetch Location Info
            </Button>

            {/* Error Display */}
            {fetchLocationInfoMutation.isError && (
              <Box
                p={4}
                bg="red.50"
                borderRadius="md"
                borderWidth="1px"
                borderColor="red.200"
                mt={4}
              >
                <Text color="red.700" fontSize="sm" fontWeight="medium" mb={1}>
                  ⚠️ Fetch Failed
                </Text>
                <Text color="red.600" fontSize="sm">
                  {fetchLocationInfoMutation.error instanceof Error
                    ? fetchLocationInfoMutation.error.message.includes('CORS')
                      ? 'CORS Policy Error: The API server is blocking requests from this domain.'
                      : fetchLocationInfoMutation.error.message.includes(
                            'Failed to fetch',
                          )
                        ? 'Connection Error: Unable to reach the API server. Check network or CORS settings.'
                        : fetchLocationInfoMutation.error.message
                    : 'Unable to fetch location information. Please try again.'}
                </Text>
              </Box>
            )}
          </Box>
        )}

        {/* Basic Information */}
        <Box>
          <Stack gap={4} align="stretch">
            <Controller
              control={form.control}
              name="name"
              render={({ field }) => (
                <Field.Root invalid={!!errors.name}>
                  <Field.Label>Location Name *</Field.Label>
                  <Input {...field} placeholder="Enter location name" />
                  <Field.ErrorText>{errors.name?.message}</Field.ErrorText>
                </Field.Root>
              )}
            />

            <HStack gap={4}>
              <Controller
                control={form.control}
                name="email"
                render={({ field }) => (
                  <Field.Root invalid={!!errors.email} flex={1}>
                    <Field.Label>Email *</Field.Label>
                    <Input {...field} type="email" placeholder="Enter email" />
                    <Field.ErrorText>{errors.email?.message}</Field.ErrorText>
                  </Field.Root>
                )}
              />

              <Controller
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <Field.Root invalid={!!errors.phone} flex={1}>
                    <Field.Label>Phone *</Field.Label>
                    <Input {...field} type="tel" placeholder="Enter phone" />
                    <Field.ErrorText>{errors.phone?.message}</Field.ErrorText>
                  </Field.Root>
                )}
              />
            </HStack>

            <Controller
              control={form.control}
              name="address"
              render={({ field }) => (
                <Field.Root invalid={!!errors.address}>
                  <Field.Label>Address *</Field.Label>
                  <Input {...field} placeholder="Enter address" />
                  <Field.ErrorText>{errors.address?.message}</Field.ErrorText>
                </Field.Root>
              )}
            />

            <HStack gap={4}>
              <Controller
                control={form.control}
                name="postal_code"
                render={({ field }) => (
                  <Field.Root invalid={!!errors.postal_code} flex={1}>
                    <Field.Label>Postal Code *</Field.Label>
                    <Input {...field} placeholder="Enter postal code" />
                    <Field.ErrorText>
                      {errors.postal_code?.message}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              />

              <Controller
                control={form.control}
                name="country_code"
                render={({ field }) => (
                  <Field.Root invalid={!!errors.country_code} flex={1}>
                    <Field.Label>Country Code *</Field.Label>
                    <Input {...field} placeholder="US" maxLength={2} />
                    <Field.ErrorText>
                      {errors.country_code?.message}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              />

              <Controller
                control={form.control}
                name="language_code"
                render={({ field }) => (
                  <Field.Root invalid={!!errors.language_code} flex={1}>
                    <Field.Label>Language Code *</Field.Label>
                    <Input {...field} placeholder="EN" maxLength={2} />
                    <Field.ErrorText>
                      {errors.language_code?.message}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              />
            </HStack>

            <Controller
              control={form.control}
              name="url"
              render={({ field }) => (
                <Field.Root invalid={!!errors.url}>
                  <Field.Label>Website URL</Field.Label>
                  <Input {...field} placeholder="https://example.com" />
                  <Field.ErrorText>{errors.url?.message}</Field.ErrorText>
                </Field.Root>
              )}
            />
          </Stack>
        </Box>

        <Divider />

        {/* Social Media Links */}
        <Box>
          <Text fontSize="md" fontWeight="medium" mb={4}>
            Social Media Links
          </Text>
          <Stack gap={4}>
            <HStack gap={4}>
              <Controller
                control={form.control}
                name="facebook_url"
                render={({ field }) => (
                  <Field.Root invalid={!!errors.facebook_url} flex={1}>
                    <Field.Label>Facebook</Field.Label>
                    <Input
                      {...field}
                      placeholder="https://facebook.com/your-"
                    />
                    <Field.ErrorText>
                      {errors.facebook_url?.message}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              />

              <Controller
                control={form.control}
                name="instagram_url"
                render={({ field }) => (
                  <Field.Root invalid={!!errors.instagram_url} flex={1}>
                    <Field.Label>Instagram</Field.Label>
                    <Input
                      {...field}
                      placeholder="https://instagram.com/your"
                    />
                    <Field.ErrorText>
                      {errors.instagram_url?.message}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              />
            </HStack>

            <HStack gap={4}>
              <Controller
                control={form.control}
                name="twitter_url"
                render={({ field }) => (
                  <Field.Root invalid={!!errors.twitter_url} flex={1}>
                    <Field.Label>Twitter</Field.Label>
                    <Input
                      {...field}
                      placeholder="https://twitter.com/your-han"
                    />
                    <Field.ErrorText>
                      {errors.twitter_url?.message}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              />

              <Controller
                control={form.control}
                name="linkedin_url"
                render={({ field }) => (
                  <Field.Root invalid={!!errors.linkedin_url} flex={1}>
                    <Field.Label>LinkedIn</Field.Label>
                    <Input
                      {...field}
                      placeholder="https://linkedin.com/compa"
                    />
                    <Field.ErrorText>
                      {errors.linkedin_url?.message}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              />
            </HStack>

            <HStack gap={4}>
              <Controller
                control={form.control}
                name="youtube_url"
                render={({ field }) => (
                  <Field.Root invalid={!!errors.youtube_url} flex={1}>
                    <Field.Label>YouTube</Field.Label>
                    <Input
                      {...field}
                      placeholder="https://youtube.com/your-c"
                    />
                    <Field.ErrorText>
                      {errors.youtube_url?.message}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              />

              <Controller
                control={form.control}
                name="tiktok_url"
                render={({ field }) => (
                  <Field.Root invalid={!!errors.tiktok_url} flex={1}>
                    <Field.Label>TikTok</Field.Label>
                    <Input
                      {...field}
                      placeholder="https://tiktok.com/@your-ac"
                    />
                    <Field.ErrorText>
                      {errors.tiktok_url?.message}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              />
            </HStack>

            <HStack gap={4}>
              <Controller
                control={form.control}
                name="snapchat_url"
                render={({ field }) => (
                  <Field.Root invalid={!!errors.snapchat_url} flex={1}>
                    <Field.Label>Snapchat</Field.Label>
                    <Input
                      {...field}
                      placeholder="https://snapchat.com/add/y"
                    />
                    <Field.ErrorText>
                      {errors.snapchat_url?.message}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              />

              <Controller
                control={form.control}
                name="spotify_url"
                render={({ field }) => (
                  <Field.Root invalid={!!errors.spotify_url} flex={1}>
                    <Field.Label>Spotify</Field.Label>
                    <Input
                      {...field}
                      placeholder="https://open.spotify.com/yo"
                    />
                    <Field.ErrorText>
                      {errors.spotify_url?.message}
                    </Field.ErrorText>
                  </Field.Root>
                )}
              />
            </HStack>
          </Stack>
        </Box>

        <Divider />

        {/* Agent Configuration */}
        <Box>
          <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={4}>
            Agent Configuration
          </Text>
          <Stack gap={4}>
            <Field.Root>
              <Field.Label>Agent Delay Time</Field.Label>
              <Input
                {...form.register('agent_delay_time', {
                  valueAsNumber: true,
                })}
                placeholder="Enter agent delay time"
                type="number"
                min="0"
                borderRadius="md"
              />
            </Field.Root>

            <Field.Root>
              <Field.Label>Agent Delay After Employee Time</Field.Label>
              <Input
                {...form.register('agent_delay_after_employee_time', {
                  valueAsNumber: true,
                })}
                placeholder="Enter agent delay time"
                type="number"
                min="0"
                borderRadius="md"
              />
            </Field.Root>
            <Field.Root>
              <Field.Label>Agent Default Active</Field.Label>
              <HStack>
                <Controller
                  control={form.control}
                  name="agent_default_active"
                  render={({ field }) => (
                    <Switch
                      name="agent_default_active"
                      checked={field.value}
                      onCheckedChange={(details) => {
                        field.onChange(details.checked);
                      }}
                    />
                  )}
                />
                <Text fontSize="sm" color="gray.600">
                  Overall delay activation. If false, no delay will be applied
                </Text>
              </HStack>
            </Field.Root>

            <HStack gap={4}>
              <Field.Root invalid={!!errors.max_interaction_per_month} flex={1}>
                <Field.Label>Max Interactions Per Month</Field.Label>
                <Input
                  {...form.register('max_interaction_per_month', {
                    valueAsNumber: true,
                  })}
                  placeholder="Enter max interactions per month"
                  type="number"
                  min="0"
                  borderRadius="md"
                />
              </Field.Root>

              <Field.Root
                invalid={!!errors.max_interaction_per_session}
                flex={1}
              >
                <Field.Label>Max Interactions Per Session</Field.Label>
                <Input
                  {...form.register('max_interaction_per_session', {
                    valueAsNumber: true,
                  })}
                  placeholder="Enter max interactions per session"
                  type="number"
                  min="0"
                  borderRadius="md"
                />
              </Field.Root>
            </HStack>

            <Field.Root>
              <Field.Label>Pay Per Use</Field.Label>
              <HStack>
                <Controller
                  control={form.control}
                  name="pay_per_use"
                  render={({ field }) => (
                    <Switch
                      name="pay_per_use"
                      checked={field.value}
                      onCheckedChange={(details) => {
                        field.onChange(details.checked);
                      }}
                    />
                  )}
                />
                <Text fontSize="sm" color="gray.600">
                  No limit use indication
                </Text>
              </HStack>
            </Field.Root>
          </Stack>
        </Box>
        <Divider />

        {/* Action Buttons */}
        <HStack justify="flex-end" gap={3} mt={6}>
          <Button
            variant="outline"
            size="md"
            borderRadius="xl"
            bg="gray.50"
            borderColor="gray.300"
            color="gray.600"
            _hover={{
              bg: 'gray.100',
              borderColor: 'gray.400',
              color: 'gray.700',
            }}
            onClick={onCancel}
            disabled={isSubmitting}
            px={6}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="solid"
            size="md"
            borderRadius="xl"
            bg="blue.500"
            color="white"
            _hover={{
              bg: 'blue.600',
              transform: 'translateY(-1px)',
              boxShadow: 'lg',
            }}
            _active={{
              bg: 'blue.700',
              transform: 'translateY(0)',
            }}
            transition="all 0.2s"
            fontWeight="semibold"
            loading={isSubmitting}
            loadingText="Creating..."
            px={6}
          >
            Create Location
          </Button>
        </HStack>
      </Stack>
    </Box>
  );
};
